import { useState, useEffect } from 'react'
import { useAlbumPhotos } from '../hooks/useAlbumPhotos'
import { ImageModal } from './ImageModal'
import { apiClient } from '../api/client'
import type { Image } from '../api/types'

type Props = {
  albumId: string
  albumTitle: string
  onBack: () => void
}

/**
 * アルバム内の写真をグリッド表示
 */
export function AlbumPhotoGrid({ albumId, albumTitle, onBack }: Props) {
  const { photos, loading, error } = useAlbumPhotos(albumId)
  const [modalImages, setModalImages] = useState<Image[]>([])
  const [modalIndex, setModalIndex] = useState<number>(-1)
  const [loadedBlobUrls, setLoadedBlobUrls] = useState<Map<string, string>>(new Map())

  // 写真を読み込んで表示用のBlob URLを生成
  useEffect(() => {
    const loadImages = async () => {
      const newBlobUrls = new Map<string, string>()

      for (const photo of photos) {
        try {
          const blobUrl = await apiClient.fetchImageBlob(photo.r2_object_key)
          newBlobUrls.set(photo.r2_object_key, blobUrl)
        } catch (err) {
          console.error(`Failed to load image ${photo.r2_object_key}:`, err)
        }
      }

      setLoadedBlobUrls(newBlobUrls)
    }

    if (photos.length > 0) {
      loadImages()
    }

    // クリーンアップ: Blob URLを解放
    return () => {
      loadedBlobUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [photos])

  const handleImageClick = (index: number) => {
    // ImageModal用のImage配列を構築
    const images: Image[] = photos.map((photo) => ({
      key: photo.r2_object_key,
      url: apiClient.getImageUrl(photo.r2_object_key),
      date: '',
      notes: '',
      memberName: albumTitle,
    }))

    setModalImages(images)
    setModalIndex(index)
  }

  const handleCloseModal = () => {
    setModalIndex(-1)
    setModalImages([])
  }

  return (
    <div className="album-photo-grid">
      <div className="album-photo-grid-header">
        <button onClick={onBack} className="back-button">
          ← 戻る
        </button>
        <h2>{albumTitle}</h2>
        <div className="photo-count">{photos.length}枚</div>
      </div>

      {loading && <div className="loading">読み込み中...</div>}

      {error && <div className="error-message">{error}</div>}

      {!loading && !error && photos.length === 0 && (
        <div className="empty-state">
          このアルバムには写真がありません。
        </div>
      )}

      {!loading && !error && photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((photo, index) => {
            const blobUrl = loadedBlobUrls.get(photo.r2_object_key)
            return (
              <div
                key={photo.r2_object_key}
                className="photo-item"
                onClick={() => handleImageClick(index)}
              >
                {blobUrl ? (
                  <img src={blobUrl} alt={`Photo ${index + 1}`} />
                ) : (
                  <div className="photo-loading">...</div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {modalIndex >= 0 && (
        <ImageModal
          images={modalImages}
          initialIndex={modalIndex}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}
